<template>
  <div class="w-full static max-w-screen-2xl mx-auto">
    <div
      class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 py-1"
    >
      <div
        v-for="(photo, index) in photoData"
        :key="photo.url"
        class="cursor-pointer group aspect-square"
        role="button"
        :class="{ 'hidden md:block': index > limit - 1 && limit > 0 }"
        @click="openPhoto(index)"
      >
        <!-- Photo -->
        <div
          v-if="photo.media_type == 'IMAGE'"
          class="block relative h-full w-full z-0 bg-center bg-cover transition-all shadow-lg duration-500 ring-white ring-4 hover:ring-8 ring-inset bg-gray-200 hover:ring-secondary/80"
          v-lazy:background-image="photo.media_url"
          role="button"
          :alt="photo.caption"
        >
          <span class="sr-only" tabindex="0" v-html="photo.caption"></span>
        </div>

        <!-- Album -->
        <div
          v-if="photo.media_type == 'CAROUSEL_ALBUM'"
          class="block relative h-full w-full z-0 bg-center bg-cover transition-all shadow-lg duration-500 ring-white ring-[6px] hover:ring-8 ring-inset bg-gray-200 hover:ring-secondary/80"
          role="button"
          v-lazy:background-image="photo.media_url"
          :alt="photo.caption"
        >
          <span class="sr-only" tabindex="0" v-html="photo.caption"></span>
        </div>

        <!-- Video -->
        <div
          v-if="photo.media_type == 'VIDEO'"
          class="relative h-full w-full z-0 bg-center bg-cover transition-all  shadow-lg duration-500 ring-white ring-[6px] hover:ring-8 ring-inset bg-gray-200 hover:ring-secondary/80 flex justify-center items-center group"
          v-lazy:background-image="photo.thumbnail_url"
          :alt="photo.caption"
        >
          <svg-vue
            v-if="photo.media_url != ''"
            icon="play"
            class="h-8 w-8 fill-current text-white drop-shadow transition-all duration-500 group-hover:h-12 group-hover:w-12"
          ></svg-vue>
          <span class="sr-only" tabindex="0" v-html="photo.caption"></span>
        </div>
      </div>
    </div>

    <teleport to="#popup-target">

      <!-- Mobile Close Button -->
      <div
        v-if="popupOpen"
        class="fixed z-50 top-0 h-full overflow-y-scroll lg:overflow-hidden w-full bg-primary bg-opacity-90 backdrop-blur-sm md:p-8 lg:p-12 flex flex-col justify-center"
      >
        <span
          @click="popupOpen = false"
          class="absolute flex items-center justify-center leading-none rounded-full h-10 w-10 p-3 hover:bg-primary-dark right-4 top-4 lg:top-0 lg:right-40 cursor-pointer transition-colors duration-300 lg:hidden z-20"
        >
          <svg-vue
            icon="times"
            class="fill-current text-white h-5 w-5"
          ></svg-vue>
        </span>

        <div class="relative flex flex-col lg:flex-row lg:p-4 w-full h-screen z-10 items-center">

          <div class="w-full lg:w-2/3 xl:w-3/4 pb-2 lg:pb-0 lg:pr-4" >
            <div
              class="md:h-full md:top-0 w-full flex flex-col items-center "
            >
              <!-- Photo -->
              <img
                v-if="activePhoto.media_type == 'IMAGE'"
                :src="activePhoto.media_url"
                class="gallery-display md:rounded-lg h-full md:h-auto max-h-full"
                :alt="activePhoto.caption"
                tabindex="0"
              />

              <!-- Album -->
              <img
                v-if="activePhoto.media_type == 'CAROUSEL_ALBUM'"
                :src="activePhoto.media_url != '' ? activePhoto.media_url : activePhoto.thumbnail_url"
                class="gallery-display md:rounded-lg h-full md:h-auto max-h-full"
                :alt="activePhoto.caption"
                tabindex="0"
              />

              <!-- Video -->
              <video
                v-if="activePhoto.media_type == 'VIDEO' && activePhoto.media_url != ''"
                autoplay
                controls
                class="gallery-display md:rounded-lg w-full"
                :alt="activePhoto.caption"
                tabindex="0"
              >
                <source :src="activePhoto.media_url" type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <!-- Video with no video? -->
              <img
                v-if="activePhoto.media_type == 'VIDEO' && activePhoto.media_url == ''"
                :src="activePhoto.media_url != '' ? activePhoto.media_url : activePhoto.thumbnail_url"
                class="gallery-display md:rounded-lg h-full md:h-auto max-h-full"
                :alt="activePhoto.caption"
                tabindex="0"
              />

            </div>
          </div>

          <div class="w-full lg:w-1/3 xl:w-1/4 grow my-auto" >

            <div class="p-4 md:p-6 xl:py-12 lg:px-8 bg-white md:rounded-lg h-full lg:h-auto" >

              <!-- Close Button -->
              <span
                @click="popupOpen = false"
                class="hidden lg:flex items-center justify-end font-bold leading-none h-10 cursor-pointer transition-colors duration-300 text-gray-900 relative right-2 top-2"
              >
                <svg-vue
                  icon="times"
                  class="fill-current text-gray-900 h-4 w-4 mr-2"
                ></svg-vue>
                close
              </span>

              <!-- Caption -->
              <div class="prose md:prose-lg xl:my-8" >
                <p
                  v-html="activePhoto.caption"
                  class="bg-white p-4 mt-4 text-center md:rounded-md max-w-3xl"
                ></p>
              </div>

              <!-- Nav Buttons -->
              <div
                class="relative max-w-xs mx-auto rounded-full bg-secondary/70 shadow-xl flex justify-between mt-4 z-20 items-center p-2"
              >
                <span
                  @click="prevPhoto"
                  class="gallery-controls pl-4"
                  v-lazy:background-image="'/images/accent-button-texture.svg'"
                  >prev</span
                >
                <span
                  class="text-white font-bold flex items-center leading-none whitespace-nowrap"
                >
                  {{ activeIndex + 1 }} of {{ photoData.length }}</span
                >
                <span
                  @click="nextPhoto"
                  class="gallery-controls pr-4"
                  v-lazy:background-image="'/images/accent-button-texture.svg'"
                  >next</span
                >
              </div>
            </div>

          </div>


        </div>
      </div>
    </teleport>
  </div>
</template>
<script>
import { defineComponent } from "vue";

export default defineComponent({
  name: "InstaGallery",

  props: {
    photos: {
      type: Object,
      default: () => {},
    },
    limit: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      activePhoto: {},
      photoData: [],
      popupOpen: false,
      activeIndex: 0,
    };
  },

  mounted() {
    this.photoData = this.photos;
  },

  methods: {
    openPhoto(num) {
      this.popupOpen = true;
      this.activeIndex = num;
      this.activePhoto = this.photoData[num];
    },

    nextPhoto() {
      if (this.activeIndex + 1 == this.photoData.length) {
        this.activeIndex = 0;
      } else {
        this.activeIndex++;
      }

      this.openPhoto(this.activeIndex);
    },

    prevPhoto() {
      if (this.activeIndex + 1 == 1) {
        this.activeIndex = this.photoData.length;
      } else {
        this.activeIndex--;
      }

      this.openPhoto(this.activeIndex);
    },
  },
});
</script>
